import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const STATUS_COLORS: Record<string, string> = {
  draft: '#64748B',
  sent: '#3B82F6',
  signed: '#10B981',
  lost: '#EF4444',
  archived: '#6B7280',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  signed: 'Signed',
  lost: 'Lost',
  archived: 'Archived',
};

export default function EstimatesPage() {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState<any[]>([]);
  const [jobMap, setJobMap] = useState<Record<string, string>>({});
  const [jobStageMap, setJobStageMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const includeArchived = filter === 'archived';

  useEffect(() => { fetchEstimates(includeArchived); }, [includeArchived]);

  const fetchEstimates = async (withArchived = false) => {
    try {
      setLoading(true);
      const [estRes, jobRes] = await Promise.all([
        api.get(`/estimates${withArchived ? '?include_archived=true' : ''}`),
        api.get('/jobs'),
      ]);
      setEstimates(estRes.data);
      // Build estimate_id -> job_id map
      const map: Record<string, string> = {};
      const stageMap: Record<string, string> = {};
      jobRes.data.forEach((j: any) => {
        if (j.estimate_id) {
          map[j.estimate_id] = j.id;
          stageMap[j.estimate_id] = j.pipeline_stage_name || '';
        }
      });
      setJobMap(map);
      setJobStageMap(stageMap);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filtered = estimates.filter(e => {
    const matchFilter = filter === 'all' || e.status === filter;
    const matchSearch = !search ||
      e.leads?.homeowner_name?.toLowerCase().includes(search.toLowerCase()) ||
      e.leads?.address?.toLowerCase().includes(search.toLowerCase()) ||
      e.users?.name?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const totalValue = estimates.reduce((s, e) => s + (e.total_price || 0), 0);
  const signedValue = estimates.filter(e => e.status === 'signed').reduce((s, e) => s + (e.total_price || 0), 0);
  const closeRate = estimates.length > 0
    ? ((estimates.filter(e => e.status === 'signed').length / estimates.length) * 100).toFixed(0)
    : '0';

  const removeEstimateFromState = (id: string) => {
    setEstimates(prev => prev.filter(est => est.id !== id));
    setJobMap(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setJobStageMap(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const isClosedOrPaidStage = (stageName?: string) => {
    const stage = (stageName || '').toLowerCase();
    return stage.includes('closed') || stage.includes('payment collected') || stage.includes('paid');
  };

  const tryDeleteAssociatedJob = async (estimateId: string) => {
    const jobId = jobMap[estimateId];
    if (!jobId) return;
    const jobStage = jobStageMap[estimateId];
    if (isClosedOrPaidStage(jobStage)) return;
    try {
      await api.delete(`/jobs/${jobId}`);
    } catch (e: any) {
      if (e?.response?.status !== 404) {
        throw e;
      }
    }
  };

  const handleDeleteDraft = async (id: string) => {
    if (!window.confirm('Delete this draft estimate?')) return;
    try {
      await api.delete(`/estimates/${id}`);
      await tryDeleteAssociatedJob(id);
      removeEstimateFromState(id);
    } catch (e) {
      console.error(e);
      window.alert('Failed to delete draft estimate');
    }
  };

  const handleVoidSent = async (id: string) => {
    if (!window.confirm('Void this sent estimate?')) return;
    try {
      await api.patch(`/estimates/${id}`, { status: 'archived' });
      await tryDeleteAssociatedJob(id);
      removeEstimateFromState(id);
    } catch (e) {
      console.error(e);
      window.alert('Failed to void estimate');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ color: '#f1f5f9', fontSize: '24px', fontWeight: 800, marginBottom: '20px' }}>Estimates</h1>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total Estimates', value: estimates.length, color: '#f1f5f9' },
          { label: 'Pipeline Value', value: `$${(totalValue / 1000).toFixed(0)}k`, color: '#4A7EB5' },
          { label: 'Signed Value', value: `$${(signedValue / 1000).toFixed(0)}k`, color: '#10B981' },
          { label: 'Close Rate', value: `${closeRate}%`, color: '#10B981' },
          { label: 'Signed', value: estimates.filter(e => e.status === 'signed').length, color: '#10B981' },
          { label: 'Draft', value: estimates.filter(e => e.status === 'draft').length, color: '#64748B' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: '#1e293b', borderRadius: '12px', padding: '16px 20px', border: '1px solid #334155', flex: 1 }}>
            <div style={{ color, fontSize: '22px', fontWeight: 800 }}>{value}</div>
            <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search by customer, address, or salesman..."
          style={{ flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '10px 14px', color: '#f1f5f9', fontSize: '14px', outline: 'none' }}
        />
        {['all', 'draft', 'sent', 'signed', 'lost', 'archived'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              padding: '8px 16px', borderRadius: '20px', border: '1px solid',
              borderColor: filter === s ? '#4A7EB5' : '#334155',
              background: filter === s ? '#4A7EB5' : '#1e293b',
              color: filter === s ? '#fff' : '#94a3b8',
              fontWeight: 600, fontSize: '13px', cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >{s}</button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : (
        <div style={{ background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                {['Customer', 'Address', 'Type', 'Total', 'SF', 'Salesman', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', color: '#64748b', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No estimates found</td></tr>
              ) : filtered.map(e => (
                <tr key={e.id}
                  style={{ borderBottom: '1px solid #1a2744', cursor: jobMap[e.id] ? 'pointer' : 'default' }}
                  onMouseEnter={e2 => (e2.currentTarget.style.background = '#243447')}
                  onMouseLeave={e2 => (e2.currentTarget.style.background = 'transparent')}
                  onClick={() => jobMap[e.id] && navigate(`/pipeline/${jobMap[e.id]}`)}
                >
                  <td style={{ padding: '14px 16px', color: '#f1f5f9', fontWeight: 600 }}>{e.leads?.homeowner_name || '—'}</td>
                  <td style={{ padding: '14px 16px', color: '#94a3b8', fontSize: '13px' }}>{e.leads?.address || '—'}</td>
                  <td style={{ padding: '14px 16px', color: '#94a3b8', textTransform: 'capitalize' }}>{e.job_type}</td>
                  <td style={{ padding: '14px 16px', color: '#10B981', fontWeight: 700 }}>${e.total_price?.toLocaleString()}</td>
                  <td style={{ padding: '14px 16px', color: '#94a3b8' }}>{e.total_sf?.toFixed(0)}</td>
                  <td style={{ padding: '14px 16px', color: '#94a3b8' }}>{e.users?.name || '—'}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      background: STATUS_COLORS[e.status] + '22',
                      color: STATUS_COLORS[e.status],
                      border: `1px solid ${STATUS_COLORS[e.status]}44`,
                      borderRadius: '20px', padding: '3px 10px',
                      fontSize: '12px', fontWeight: 700,
                    }}>{STATUS_LABELS[e.status] || e.status}</span>
                  </td>
                  <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '13px' }}>
                    {new Date(e.created_at).toLocaleDateString()}
                    {e.signed_at && <div style={{ color: '#10B981', fontSize: '11px' }}>Signed {new Date(e.signed_at).toLocaleDateString()}</div>}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {e.status === 'draft' && (
                      <button
                        onClick={(evt) => {
                          evt.stopPropagation();
                          handleDeleteDraft(e.id);
                        }}
                        style={{
                          border: '1px solid #475569',
                          background: '#0f172a',
                          color: '#e2e8f0',
                          borderRadius: '8px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                        title="Delete draft"
                      >
                        🗑️
                      </button>
                    )}
                    {e.status === 'sent' && (
                      <button
                        onClick={(evt) => {
                          evt.stopPropagation();
                          handleVoidSent(e.id);
                        }}
                        style={{
                          border: '1px solid #475569',
                          background: '#0f172a',
                          color: '#e2e8f0',
                          borderRadius: '8px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 700,
                        }}
                        title="Void estimate"
                      >
                        ✕ Void
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
